import React from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  CircularProgress,
  LinearProgress,
} from '@mui/material';
import { Email, Send } from '@mui/icons-material';

interface EmailSendingModalProps {
  open: boolean;
  progress?: number;
  message?: string;
  totalEmails?: number;
  sentEmails?: number;
}

export const EmailSendingModal: React.FC<EmailSendingModalProps> = ({
  open,
  progress = 0,
  message = 'Sending emails...',
  totalEmails = 0,
  sentEmails = 0,
}) => {
  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          overflow: 'hidden',
        },
      }}
    >
      <DialogContent
        sx={{
          p: 4,
          textAlign: 'center',
          backgroundColor: 'background.paper',
          minHeight: 200,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 3,
        }}
      >
        {/* Main Loading Spinner */}
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 80,
            height: 80,
          }}
        >
          <CircularProgress
            size={80}
            thickness={3}
            sx={{
              color: 'primary.main',
              position: 'absolute',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 60,
              height: 60,
              backgroundColor: 'background.paper',
              borderRadius: '50%',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            }}
          >
            <Email
              sx={{
                fontSize: 28,
                color: 'primary.main',
                animation: 'pulse 2s infinite',
                '@keyframes pulse': {
                  '0%': {
                    opacity: 1,
                    transform: 'scale(1)',
                  },
                  '50%': {
                    opacity: 0.7,
                    transform: 'scale(1.1)',
                  },
                  '100%': {
                    opacity: 1,
                    transform: 'scale(1)',
                  },
                },
              }}
            />
          </Box>
        </Box>

        {/* Main Title */}
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            color: 'text.primary',
            mb: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Send sx={{ fontSize: 24, color: 'primary.main' }} />
          Sending Emails
        </Typography>

        {/* Status Message */}
        <Typography
          variant="body1"
          sx={{
            color: 'text.secondary',
            mb: 2,
            fontSize: '16px',
            fontWeight: 500,
          }}
        >
          {message}
        </Typography>

        {/* Progress Bar */}
        {totalEmails > 0 && (
          <Box sx={{ width: '100%', maxWidth: 300 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1,
              }}
            >
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                Progress
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                {sentEmails} of {totalEmails} emails
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: 'grey.200',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                  backgroundColor: 'primary.main',
                },
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                mt: 1,
                display: 'block',
                fontWeight: 500,
              }}
            >
              {Math.round(progress)}% complete
            </Typography>
          </Box>
        )}

        {/* Additional Info */}
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            mt: 2,
            fontStyle: 'italic',
            maxWidth: 300,
            lineHeight: 1.4,
          }}
        >
          Please wait while we process your email requests. This may take a few moments depending on the number of recipients.
        </Typography>
      </DialogContent>
    </Dialog>
  );
};

export default EmailSendingModal;
